import { Grid, TextInput } from '@mantine/core';
import { useFocusWithin } from '@mantine/hooks';
import React, { KeyboardEvent, useCallback, useEffect, useRef } from 'react';

const parseDate = (value) => {
  const regex = /(\d{4})-(\d{1,2})-(\d{1,2})/;

  const match = regex.exec(value);
  if (!match) {
    return null;
  }

  const asDate = new Date(`${match[1]}-${match[2]}-${match[3]}`);

  if (!(asDate instanceof Date) || isNaN(asDate as any)) return null;

  return [match[1], match[2], match[3]];
};

const formatDate = (year, month, day) => {
  if (!year || !month || !day) return '';

  const m = month.toString().padStart(2, '0');
  const d = day.toString().padStart(2, '0');

  return `${year}-${m}-${d}`;
};

export type EnsDateInputProps = {
  value?: string;
  onChange?: (value: string) => void;
  isBday?: boolean;
  hasError?: boolean;
  onBlur?: (value: string) => void;
  disabled?: boolean;
};

const EnsDateInput = ({
  value = null,
  onChange = null,
  isBday = false,
  hasError = false,
  onBlur = null,
  disabled = false,
  ...rest
}: EnsDateInputProps) => {
  const [yearInput, setYearInput] = React.useState('');
  const [monthInput, setMonthInput] = React.useState('');
  const [daysInput, setDaysInput] = React.useState('');

  const [isSetup, setIsSetup] = React.useState(false);

  const dayRef = useRef();
  const monthRef = useRef();
  const yearRef = useRef();

  const onGetChangeHandler = useCallback(
    (baseChange, nextTarget, maxLength = 2) => {
      return (e) => {
        let newValue = e.target.value;
        newValue = newValue.replace(/[^0-9]/g, '');
        newValue = newValue.slice(0, maxLength);

        baseChange(newValue);

        if (nextTarget && newValue.length === maxLength) {
          nextTarget.current.focus();
          nextTarget.current.select();
        }
      };
    },
    [onChange, yearInput, monthInput, daysInput],
  );

  const changeYear = useCallback(onGetChangeHandler(setYearInput, null, 4), [
    onGetChangeHandler,
  ]);
  const changeMonth = useCallback(onGetChangeHandler(setMonthInput, yearRef), [
    onGetChangeHandler,
  ]);
  const changeDay = useCallback(onGetChangeHandler(setDaysInput, monthRef), [
    onGetChangeHandler,
  ]);

  useEffect(() => {
    setIsSetup(true);

    const pd = parseDate(value);
    if (!pd || value === '') return;

    setYearInput(pd[0]?.replace(/^0+/, ''));
    setMonthInput(pd[1]?.replace(/^0+/, ''));
    setDaysInput(pd[2]?.replace(/^0+/, ''));
  }, [value, 1]);

  useEffect(() => {
    if (!isSetup) return;

    if (value === '') {
      setYearInput('');
      setMonthInput('');
      setDaysInput('');
      if (onChange) onChange(null);
    }
  }, [value]);

  useEffect(() => {
    if (!isSetup || disabled) return;

    let newDate = formatDate(yearInput, monthInput, daysInput);
    if (parseDate(newDate) === null) newDate = null;

    if ((newDate || '') === (value || '') || !onChange) return;

    onChange(newDate);
  }, [yearInput, monthInput, daysInput, onChange]);

  const onFocus = useCallback((e) => e.target.select(), []);

  const { ref, focused } = useFocusWithin();

  useEffect(() => {
    if (!isSetup || !onBlur || focused || disabled) return;

    const newDate = formatDate(yearInput, monthInput, daysInput);

    if (newDate === value) return;
    if (!parseDate(newDate)) onBlur(null);
    else onBlur(newDate);
  }, [focused]);

  const handleMoveEvents = useCallback((e: KeyboardEvent) => {
    let nextFocus: HTMLInputElement = null;

    if (e.key == 'ArrowRight') {
      if (e.target === dayRef.current)
        nextFocus = monthRef.current as HTMLInputElement;
      if (e.target === monthRef.current)
        nextFocus = yearRef.current as HTMLInputElement;
    }
    if (e.key == 'ArrowLeft') {
      if (e.target === yearRef.current)
        nextFocus = monthRef.current as HTMLInputElement;
      if (e.target === monthRef.current)
        nextFocus = dayRef.current as HTMLInputElement;
    }

    if (nextFocus) {
      e.preventDefault();
      nextFocus.focus();
    }
  }, []);

  return (
    <Grid columns={12} {...rest} ref={ref}>
      <Grid.Col span={4}>
        <TextInput
          ref={dayRef}
          value={daysInput}
          onChange={changeDay}
          onFocus={onFocus}
          placeholder="Tag"
          autoComplete={isBday ? 'bday-day' : null}
          type="numeric"
          inputMode="numeric"
          pattern="[0-9]*"
          error={!!hasError}
          disabled={disabled}
          onKeyUp={handleMoveEvents}
        />
      </Grid.Col>
      <Grid.Col span={4}>
        <TextInput
          ref={monthRef}
          value={monthInput}
          onChange={changeMonth}
          onFocus={onFocus}
          placeholder="Monat"
          autoComplete={isBday ? 'bday-month' : null}
          type="numeric"
          inputMode="numeric"
          pattern="[0-9]*"
          error={!!hasError}
          disabled={disabled}
          onKeyUp={handleMoveEvents}
        />
      </Grid.Col>
      <Grid.Col span={4}>
        <TextInput
          ref={yearRef}
          value={yearInput}
          onChange={changeYear}
          onFocus={onFocus}
          placeholder="Jahr"
          autoComplete={isBday ? 'bday-year' : null}
          type="numeric"
          inputMode="numeric"
          pattern="[0-9]*"
          error={!!hasError}
          disabled={disabled}
          onKeyUp={handleMoveEvents}
        />
      </Grid.Col>
    </Grid>
  );
};

export default EnsDateInput;
